// frontend/src/utils/errors.js
//
// One rule for turning a rejected api call into something a customer can read.
//
// There were four copies of this, and they disagreed: two preferred `err.error`
// over `err.message`, which meant a Greek customer was shown the literal strings
// SAME_EMAIL, PASSWORD_REQUIRED and INVALID_PASSWORD, and one fell through to
// the raw axios `message`, which put "timeout of 15000ms exceeded" in red under
// a button denominated in euro.
//
// The order below is: the validator's sentence, then the server's sentence, then
// the caller's own translated fallback. `err.error` is a MACHINE CODE on this
// API and is never shown as-is; it is only used when it happens to be a
// sentence, which some older routes still send.

// SAME_EMAIL, INVALID_PASSWORD, RENTAL_NOT_PAYABLE… — an identifier, not prose.
const isMachineCode = (s) => /^[A-Z][A-Z0-9_]*$/.test(s);

/** The best server-authored sentence in a rejection, or null if there is none. */
export function serverMessage(err) {
  if (!err || typeof err !== 'object') return null;
  // A timeout or a dropped connection carries no server text at all, only
  // axios's own English diagnostics. Those are for the console.
  if (err.network) return null;
  if (Array.isArray(err.errors) && err.errors[0]?.msg) return err.errors[0].msg;
  if (typeof err.message === 'string' && err.message.trim()) return err.message;
  if (typeof err.error === 'string' && err.error.trim() && !isMachineCode(err.error)) return err.error;
  return null;
}

/** The machine code a caller can branch on, or null. */
export function errorCode(err) {
  if (!err || typeof err !== 'object') return null;
  return typeof err.error === 'string' ? err.error : null;
}

/** True when we never heard back, so the request may well have landed. */
export function isIndeterminate(err) {
  // A rejection with no status at all never reached the server's reply — or the
  // reply never reached us. A 5xx counts too: the server may have committed
  // before it fell over. Only a 4xx is a decision we can trust as a refusal.
  if (!err || typeof err !== 'object') return true;
  const status = Number(err.status);
  if (!Number.isFinite(status)) return true;
  return status >= 500;
}

/** Message for a toast: the server's words if it sent any, else the caller's. */
export const errorText = (err, fallback) => serverMessage(err) || fallback;
